import React, { useCallback, useEffect, useState } from 'react';
import CustomDataGrid from '../../../../../../common/components/customized/customdatagrid/CustomDataGrid';
import { useAppDispatch, useAppSelector } from '../../../../../../app/redux/hooks';
import { Box, Tooltip, Typography } from '@mui/material';
import CustomTextField from '../../../../../../common/components/customized/customtextfield/CustomTextField';
import { useForm } from 'react-hook-form';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { usePagination } from '../../../configurations/regular/hooks/regularDatas';
import {
  capitalizeFirstLetter,
  convertTo12HourFormat
} from '../../../../../../utils/commonFunctions';
import { Height } from '@mui/icons-material';
import {
  clearBookings,
  GetGrpBookings
} from '../../../../redux/reducer/autoPlannerSlices/autoplanner';

const BookingRLR = ({ payloads, setFilter }: any) => {
  const dispatch = useAppDispatch();

  const { customTours, count } = useAppSelector(
    (state: any) => state?.tripGRP?.data?.data
  ) || { customTours: [], count: 0 };
  const { isLoading } = useAppSelector((state: any) => state.tripGRP);
  const [rows, setRows] = useState<any>([]);

  const {
    pageNo,
    pageSize,
    search,
    sort,
    handlePagination,
    handleSearchChange,
    handleSortChange
  } = usePagination({
    pageno: 1,
    pagesize: 20,
    url: GetGrpBookings,
    payloads: { autoplannerID: payloads.autoplannerID, mode: 'RLR' }
  });

  const { control } = useForm();
  const columns = [
    {
      title: 'Date and Time',
      field: 'date',
      headerName: 'Date and Time',
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }: any) => {
        return (
          <Typography noWrap sx={{ fontSize: '12px' }}>
            {`${row?.date}, ${convertTo12HourFormat(row?.time)}`}
          </Typography>
        );
      }
    },
    {
      title: 'Tour Name',
      field: 'guest_name',
      headerName: 'Tour Name',
      flex: 1,
      minWidth: 200
    },
    {
      title: 'Agent Name',
      field: 'agent_name',
      headerName: 'Agent Name',
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }: any) => {
        return Boolean(row?.agent_name) ? row?.agent_name : '-';
      }
    },
    {
      title: 'Driver Name',
      field: 'driverName',
      headerName: 'Driver Name',
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }: any) => {
        return Boolean(row?.driverName) ? capitalizeFirstLetter(row?.driverName) : '-';
      }
    },
    {
      title: 'Vehicle No',
      field: 'vehicleNumber',
      headerName: 'Vehicle No',
      flex: 1,
      minWidth: 160,
      renderCell: ({ row }: any) => {
        return Boolean(row?.vehicleNumber) ? row?.vehicleNumber.toUpperCase() : '-';
      }
    },
    {
      title: 'Ref Number',
      field: 'refNumber',
      headerName: 'Ref Number',
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }: any) => {
        return Boolean(row?.ref_number) ? row?.ref_number : '-';
      }
    },
    {
      title: 'From',
      headerName: 'From',
      field: 'source',
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }: any) => {
        return (
          <Box sx={{ width: '200px', display: 'flex' }}>
            <Tooltip title={row?.from?.locationAddress}>
              <Typography noWrap sx={{ fontSize: '12px' }}>
                {row?.from?.locationAddress}
              </Typography>
            </Tooltip>
          </Box>
        );
      }
    },

    {
      title: 'To',
      headerName: 'To',
      field: 'destination',
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }: any) => {
        return (
          <Box
            sx={{
              width: '200px',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Tooltip title={row?.to?.locationAddress}>
              <Typography noWrap sx={{ fontSize: '12px' }}>
                {row?.to?.locationAddress}
              </Typography>
            </Tooltip>
          </Box>
        );
      }
    },
    {
      title: 'Total Passengers',
      field: 'totalPassengers',
      headerName: 'Total Passengers',
      sortable: false,
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }: any) => {
        return Boolean(row?.totalPassengers) ? row?.totalPassengers : '-';
      }
    }
  ]?.map((item: any) => ({
    ...item,
    sortable: false
  }));

  const clearFilter = async (payloads: any) => {
    await dispatch(GetGrpBookings({ ...payloads, mode: 'RLR' }));
  };

  const submitFilter = async (payloads: any) => {
    await dispatch(GetGrpBookings({ ...payloads, search: search, mode: 'RLR' }));
  };

  useEffect(() => {
    setFilter({
      search: search,
      sort: sort,
      clearFilters: clearFilter,
      submitFilters: submitFilter
    });
  }, [search, sort]);

  useEffect(() => {
    if (customTours) {
      const updatedRows = customTours?.map((item: any, index: number) => ({
        ...item,
        id: index + 1
      }));
      setRows(updatedRows);
    }
  }, [customTours]);
  useEffect(() => {
    dispatch(GetGrpBookings({ ...payloads, mode: 'RLR' }));

    return () => {
      dispatch(clearBookings());
    };
  }, []);
  return (
    <Box sx={{ height: '70vh', overflow: 'auto' }}>
      <Box sx={{ width: 200, float: 'right' }}>
        <CustomTextField
          control={control}
          name='search'
          id='search'
          placeholder='Search...'
          onChangeCallback={(value: string) => {
            handleSearchChange(value);
          }}
          icon={<SearchOutlinedIcon color='primary' />}
          variant={'standard'}
          value={search}
        />
      </Box>
      <CustomDataGrid
        rows={rows ?? []}
        columns={columns}
        loading={isLoading}
        type={'logistics'}
        rowCount={count}
        pageNo={pageNo}
        pageSize={pageSize}
        paginationModel={{ page: pageNo && count ? pageNo - 1 : 0, pageSize: pageSize }}
        onPaginationModelChange={handlePagination}
        onSortModelChange={handleSortChange}
      />
    </Box>
  );
};

export default BookingRLR;
